/* AnalysesCarousel — mobile-only swipe carousel для Анализи tab.
 *
 * Съдържа 4 основни анализни карти. Добавки:
 *  - Dot indicators показващи текущата карта и колко общо.
 *  - Auto-advance (5 сек/slide) когато tab-ът е току-що отворен; спира
 *    при първо потребителско взаимодействие (user intent).
 */
import React, { useState, useEffect, useCallback } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

const AUTO_ADVANCE_MS = 5000;

export default function AnalysesCarousel({ slides, onSlideChange }) {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";

  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(slides.length);
  // Stops auto-advance once the user interacts (swipe, tap dot, etc.).
  const [userInteracted, setUserInteracted] = useState(false);

  // Sync count + current index with embla API
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      if (typeof onSlideChange === "function") onSlideChange(api.selectedScrollSnap());
    };
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    // Any user-initiated pointer / keyboard action stops auto-advance.
    const onInteraction = () => setUserInteracted(true);
    api.on("pointerDown", onInteraction);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
      api.off("pointerDown", onInteraction);
    };
  }, [api, onSlideChange]);

  // Auto-advance loop — runs only until user intervenes.
  useEffect(() => {
    if (!api || userInteracted) return;
    const t = setInterval(() => {
      if (userInteracted) return;
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [api, userInteracted]);

  const goTo = useCallback((i) => {
    setUserInteracted(true);
    api?.scrollTo(i);
  }, [api]);

  return (
    <div className="md:hidden -mx-1" data-testid="analyses-carousel-wrap">
      <Carousel opts={{ align: "start", loop: true }} setApi={setApi}>
        <CarouselContent className="-ml-2">
          {slides.map((slide) => (
            <CarouselItem key={slide.key} className="pl-2 basis-[92%]">
              {slide.node}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Dot indicators — tappable, show current slide */}
      <div
        className="flex items-center justify-center gap-1.5 mt-3"
        data-testid="analyses-carousel-dots"
        aria-label={bg ? "Навигация между анализи" : "Analyses navigation"}
      >
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={`dot-${count}-${i}`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === current
                ? (isDark ? "w-5 bg-sky-400" : "w-5 bg-sky-500")
                : (isDark ? "w-1.5 bg-white/20 hover:bg-white/40" : "w-1.5 bg-gray-300 hover:bg-gray-400")
            }`}
            data-testid={`analyses-carousel-dot-${i}`}
            aria-current={i === current ? "true" : undefined}
            aria-label={bg ? `Карта ${i + 1}` : `Card ${i + 1}`}
          />
        ))}
      </div>

      {/* Hint text shows "auto-play" state on first open so users understand
          the motion isn't a glitch. Hides once user engages. */}
      <div
        className={`text-center text-[10px] mt-1.5 transition-opacity duration-500
          ${userInteracted ? "opacity-0 h-0 overflow-hidden" : (isDark ? "text-gray-500" : "text-gray-400")}`}
        data-testid="analyses-carousel-hint"
      >
        {bg ? "← плъзнете или тапнете точка →" : "← swipe or tap a dot →"}
      </div>
    </div>
  );
}
